import { SalesforceAutomation } from 'module/api/integrations/salesforce/types';
import { User } from 'module/api/user/types';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { MoreActionsDropdown } from 'module/ui/components/atoms/more-actions';
import { ColumnType } from 'module/ui/components/molecules/draggable-table/types';
import { faEdit, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { faGripDotsVertical } from '@fortawesome/pro-solid-svg-icons';
import { Switch } from 'antd';
import moment from 'moment';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { fullName } from 'utils/full-name';

const AutomationNameCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const AutomationNameText = styled.div`
  font-weight: 500;
`;

const AutomationTypePill = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #3b82f6;
  background-color: #e0f2fe;
  padding: 2px 6px;
  border-radius: 8px;
  text-transform: uppercase;
`;

const getUserLabel = (
  user: Pick<User, 'firstName' | 'lastName' | 'emailId'>,
): string => {
  return fullName(user) || user?.emailId || '-';
};

const AUTOMATION_TYPE_DISPLAY_MAP: Record<string, string> = {
  CRM: 'v1',
  CRM_V2: 'v2',
};

// Simple date formatter
const formatDate = (dateString: string): string => {
  return moment(dateString).format('DD MMM YYYY h.mma');
};

interface GenerateColumnsProps {
  integrationId: string;
  handleDelete: (automationId: number) => void;
  handleEnable: (automationId: number, enabled: boolean) => void;
  handleEdit: (automationId: number) => void;
  translate: (key: string) => string;
}

export const generateColumns = ({
  handleDelete,
  handleEnable,
  handleEdit,
  translate,
}: GenerateColumnsProps): ColumnType<SalesforceAutomation>[] => {
  return [
    {
      key: 'drag',
      title: '',
      width: '24px',
      render: () => (
        <div className="drag-handle">
          <FontAwesomeIcon
            icon={faGripDotsVertical}
            style={{ color: 'var(--gray-50)', cursor: 'grab' }}
          />
        </div>
      ),
    },
    {
      key: 'automationName',
      title: <FormattedMessage id="name" />,
      width: '800px',
      render: (value: string, row: SalesforceAutomation) => {
        // temporary fix to distinguish CRM, CRM_V2 automations for sf
        const displayVersion = row.automationType
          ? AUTOMATION_TYPE_DISPLAY_MAP[row.automationType]
          : null;

        return (
          <AutomationNameCell>
            <AutomationNameText>
              {value ||
                translate('settings.integrations.sf.automation.unnamed')}
            </AutomationNameText>
            {row.automationType && (
              <AutomationTypePill>
                {displayVersion || ''}
              </AutomationTypePill>
            )}
          </AutomationNameCell>
        );
      },
    },
    {
      key: 'createdBy',
      title: <FormattedMessage id="created_by" />,

      render: (createdBy: User) => getUserLabel(createdBy),
    },
    {
      key: 'updatedAt',
      title: <FormattedMessage id="last_modified" />,
      render: (date: string) => formatDate(date),
    },
    {
      key: 'enabled',
      title: <FormattedMessage id="status" />,
      render: (status: boolean, row: SalesforceAutomation) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Switch
            onChange={(checked) => handleEnable(row.automationId!, checked)}
            checked={status}
            size="small"
          />
          <span style={{ fontSize: '12px' }}>
            {status ? (
              <FormattedMessage id="enabled" />
            ) : (
              <FormattedMessage id="disabled" />
            )}
          </span>
        </div>
      ),
    },
    {
      key: 'actions',
      title: '',
      width: '40px',
      render: (_, row: SalesforceAutomation) => (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <MoreActionsDropdown
            menu={{
              onClick: ({ key }) => {
                if (key === 'edit') {
                  handleEdit(row.automationId!);
                } else if (key === 'delete') {
                  handleDelete(row.automationId!);
                }
              },
              items: [
                {
                  type: 'group',
                  label: <FormattedMessage id="more_actions" />,
                  children: [
                    {
                      key: 'edit',
                      label: <FormattedMessage id="edit" />,
                      icon: <FontAwesomeIcon icon={faEdit} />,
                    },
                    {
                      key: 'delete',
                      label: <FormattedMessage id="delete" />,
                      icon: <FontAwesomeIcon icon={faTrash} />,
                      danger: true,
                    },
                  ],
                },
              ],
            }}
            dataCyValue={`automation-actions-${row.automationId}`}
          />
        </div>
      ),
    },
  ];
};
